import { Link } from '@tanstack/react-router'
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Clock, Link2, Monitor, MonitorCheck, QrCode, RefreshCw, Replace } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Combobox, type ComboboxOption } from '@/components/combobox'
import { MultiCombobox } from '@/components/multi-combobox'
import { handlePlanError, notifyError } from '@/components/notify'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useDisplay, useDisplayPage } from '@/features/displays/api'
import { ConnectivityBadge } from '@/features/displays/components/connectivity-badge'
import { InheritableTimezone, OrientationSelect } from '@/features/displays/components/display-form'
import { PlatformIcon } from '@/features/displays/components/platform-icon'
import { platformLabel } from '@/features/displays/lib/status'
import { useGroupOptions, useLocationComboOptions, useTagOptions } from '@/features/groups/components/pickers'
import { usePermission } from '@/hooks/use-permission'
import { ApiError, errorMessage } from '@/lib/api'
import { formatRelative } from '@/lib/format'
import { browserTimeZone } from '@/lib/timezones'
import { cn } from '@/lib/utils'
import type { ClaimMode, Display, PairingClaimResult, PairingLookup } from '@/types/api'
import { usePairingClaim, usePairingLookup } from '../api'
import { formatCode, isCompleteCode } from '../lib/code'
import {
  buildClaimRequest,
  canAdvance,
  initialPairingState,
  isSubmitStep,
  nextStep,
  previousStep,
  selectMode,
  stepsFor,
  switchToReplace,
  type PairingPreset,
  type PairingState,
} from '../lib/steps'
import { CodeInput } from './code-input'

type Conflict = { kind: 'display-has-player' | 'no-player' | 'rate-limited' | 'other'; message: string } | null

const STEP_TITLES: Record<PairingState['step'], string> = {
  code: 'Enter pairing code',
  mode: 'How should this player be used?',
  details: 'New display details',
  target: 'Choose the display',
  success: 'Player paired',
}

export function PairingFlow({
  preset,
  onDone,
  onCancel,
  onTitleChange,
  footerClassName,
  successAction,
}: {
  preset?: PairingPreset
  /** Called when the user leaves the success screen (e.g. close dialog / navigate). */
  onDone?: (result: PairingClaimResult) => void
  onCancel?: () => void
  onTitleChange?: (title: string) => void
  footerClassName?: string
  /** Replaces the default success-screen buttons (e.g. onboarding "Continue"). */
  successAction?: (result: PairingClaimResult) => ReactNode
}) {
  const [state, setState] = useState<PairingState>(() => initialPairingState(preset))
  const [device, setDevice] = useState<PairingLookup | null>(null)
  const [codeError, setCodeError] = useState<string | null>(null)
  const [conflict, setConflict] = useState<Conflict>(null)
  const [result, setResult] = useState<PairingClaimResult | null>(null)
  const lookup = usePairingLookup()
  const claim = usePairingClaim()
  const canManageDisplays = usePermission('displays.manage')

  const patch = (p: Partial<PairingState>) => setState((s) => ({ ...s, ...p }))

  useEffect(() => onTitleChange?.(STEP_TITLES[state.step]), [state.step, onTitleChange])

  // Auto-lookup as soon as six digits are present — once per code/nonce (StrictMode-safe;
  // lookups count against the 5/min claim rate limit).
  const { mutate: runLookup, reset: resetLookup } = lookup
  const lastLookup = useRef<string | null>(null)
  const [retryable, setRetryable] = useState(false)
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    const key = `${state.code}|${state.nonce ?? ''}`
    if (!isCompleteCode(state.code) || state.lookedUp || lastLookup.current === key) return
    lastLookup.current = key
    setCodeError(null)
    setRetryable(false)
    runLookup(
      { code: state.code, nonce: state.nonce },
      {
        onSuccess: (d) => {
          setDevice(d)
          setState((s) => (s.code === state.code ? { ...s, lookedUp: true } : s))
        },
        onError: (e) => {
          if (e instanceof ApiError && e.status === 404) setCodeError('Code not found or expired. Check the code on the screen — it refreshes every 10 minutes.')
          else {
            setRetryable(true)
            if (e instanceof ApiError && e.status === 429) setCodeError(rateLimitMessage(e))
            else setCodeError(errorMessage(e))
          }
        },
      },
    )
  }, [state.code, state.nonce, state.lookedUp, runLookup, attempt])

  function retryLookup() {
    lastLookup.current = null
    setRetryable(false)
    setAttempt((n) => n + 1)
  }

  function onCodeChange(code: string) {
    if (code === state.code) return
    resetLookup()
    setDevice(null)
    setCodeError(null)
    // A typed code no longer matches the QR nonce.
    patch({ code, lookedUp: false, nonce: code === preset?.code ? (preset?.nonce ?? null) : null })
  }

  function submit() {
    setConflict(null)
    let body
    try {
      body = buildClaimRequest(state)
    } catch (e) {
      setConflict({ kind: 'other', message: (e as Error).message })
      return
    }
    claim.mutate(body, {
      onSuccess: (res) => {
        setResult(res)
        patch({ step: 'success' })
      },
      onError: (e) => {
        if (handlePlanError(e)) return
        if (!(e instanceof ApiError)) return notifyError(e)
        if (e.status === 404) {
          setDevice(null)
          setCodeError('Code not found or expired. Check the code on the screen and try again.')
          setState((s) => ({ ...s, step: 'code', lookedUp: false, code: '' }))
          return
        }
        if (e.is('display-has-player')) return setConflict({ kind: 'display-has-player', message: 'This display already has an active player.' })
        if (e.is('no-player')) return setConflict({ kind: 'no-player', message: 'This display no longer has a player to replace.' })
        if (e.status === 429) return setConflict({ kind: 'rate-limited', message: rateLimitMessage(e) })
        if (e.isValidation) return setConflict({ kind: 'other', message: Object.values(e.errors)[0]?.[0] ?? e.detail })
        setConflict({ kind: 'other', message: e.detail })
      },
    })
  }

  function next() {
    setConflict(null)
    if (isSubmitStep(state.step)) return submit()
    patch({ step: nextStep(state) })
  }

  function back() {
    setConflict(null)
    const prev = previousStep(state)
    if (prev) patch({ step: prev })
  }

  const steps = stepsFor(state.mode, state.locked)
  const stepIndex = steps.indexOf(state.step)

  return (
    <div className="grid gap-5">
      {state.step !== 'success' && (
        <ol className="flex items-center gap-1.5" aria-label="Pairing progress">
          {steps.slice(0, -1).map((s, i) => (
            <li key={s} className={cn('h-1 flex-1 rounded-full transition-colors', i <= stepIndex ? 'bg-primary' : 'bg-muted')} aria-current={s === state.step ? 'step' : undefined}>
              <span className="sr-only">{STEP_TITLES[s]}</span>
            </li>
          ))}
        </ol>
      )}

      {state.step === 'code' && (
        <CodeStep
          code={state.code}
          onCode={onCodeChange}
          onNonce={(nonce) => patch({ nonce })}
          hasNonce={!!state.nonce}
          error={codeError}
          looking={lookup.isPending}
          device={device}
          onRetry={retryable ? retryLookup : undefined}
        />
      )}
      {state.step === 'mode' && device && <DeviceSummary device={device} compact />}
      {state.step === 'mode' && <ModeStep mode={state.mode} canCreate={canManageDisplays} onMode={(m) => setState((s) => selectMode(s, m))} />}
      {state.step === 'details' && <DetailsStep state={state} onChange={(display) => patch({ display })} />}
      {state.step === 'target' && <TargetStep state={state} onDisplay={(displayId) => patch({ displayId })} onReason={(reason) => patch({ reason })} />}
      {state.step === 'success' && result && <SuccessStep result={result} />}

      {conflict && (
        <div role="alert" className="flex flex-col gap-3 rounded-xl border border-warning/40 bg-warning/10 p-3.5 text-sm sm:flex-row sm:items-center">
          <AlertTriangle className="size-4 shrink-0 text-[color-mix(in_oklch,var(--warning),black_30%)] dark:text-warning" />
          <p className="flex-1">{conflict.message}</p>
          {conflict.kind === 'display-has-player' && !state.locked && (
            <Button size="sm" variant="outline" onClick={() => (setConflict(null), setState((s) => switchToReplace(s)))}>
              <Replace /> Replace its player instead
            </Button>
          )}
          {conflict.kind === 'no-player' && !state.locked && (
            <Button size="sm" variant="outline" onClick={() => (setConflict(null), patch({ mode: 'attach', step: 'target' }))}>
              <Link2 /> Attach instead
            </Button>
          )}
        </div>
      )}

      <div className={cn('flex items-center justify-between gap-2 border-t pt-4', footerClassName)}>
        {state.step === 'success' && result && successAction ? (
          <>
            <span />
            {successAction(result)}
          </>
        ) : state.step === 'success' ? (
          <>
            <Button variant="ghost" onClick={() => (setState(initialPairingState({})), setDevice(null), setResult(null), resetLookup(), claim.reset())}>
              <RefreshCw /> Pair another
            </Button>
            {result && (
              <Button asChild onClick={() => onDone?.(result)}>
                <Link to="/displays/$displayId" params={{ displayId: result.display.id }}>
                  Open display <ArrowRight />
                </Link>
              </Button>
            )}
          </>
        ) : (
          <>
            {previousStep(state) ? (
              <Button variant="ghost" onClick={back} disabled={claim.isPending}>
                <ArrowLeft /> Back
              </Button>
            ) : onCancel ? (
              <Button variant="ghost" onClick={onCancel}>
                Cancel
              </Button>
            ) : (
              <span />
            )}
            <Button onClick={next} disabled={!canAdvance(state) || lookup.isPending} loading={claim.isPending}>
              {isSubmitStep(state.step) ? submitLabel(state.mode) : 'Continue'}
              {!isSubmitStep(state.step) && <ArrowRight />}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

/** Laravel's default 429 detail is terse; the pairing breaker sends a useful one ("scan the QR code"). */
function rateLimitMessage(e: ApiError): string {
  const generic = !e.detail || /^too many (requests|attempts)\.?$/i.test(e.detail.trim())
  return generic ? 'Too many pairing attempts. Wait a minute and try again, or scan the QR code on the screen.' : e.detail
}

function submitLabel(mode: ClaimMode | null) {
  return mode === 'new' ? 'Create display & pair' : mode === 'replace' ? 'Replace player' : 'Attach player'
}

/* -------------------------------------------------------------- code step */

function CodeStep({
  code,
  onCode,
  onNonce,
  hasNonce,
  error,
  looking,
  device,
  onRetry,
}: {
  onRetry?: () => void
  code: string
  onCode: (code: string) => void
  onNonce: (nonce: string | null) => void
  hasNonce: boolean
  error: string | null
  looking: boolean
  device: PairingLookup | null
}) {
  return (
    <div className="grid gap-5">
      <div className="text-center">
        <p className="text-sm text-muted-foreground">Type the 6-digit code shown on the screen, or scan its QR code with your phone.</p>
      </div>
      <CodeInput value={code} onChange={onCode} onNonce={onNonce} invalid={!!error} autoFocus={!code} aria-describedby="pair-code-message" />
      <div id="pair-code-message" className="min-h-5 text-center text-[13px]" aria-live="polite">
        {error ? (
          <span className="font-medium text-destructive">
            {error}
            {onRetry && (
              <button type="button" onClick={onRetry} className="ml-1.5 font-medium text-primary underline-offset-4 hover:underline">
                Try again
              </button>
            )}
          </span>
        ) : looking ? (
          <span className="text-muted-foreground">Looking up {formatCode(code)}…</span>
        ) : hasNonce && !device ? (
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <QrCode className="size-3.5" /> Prefilled from QR code
          </span>
        ) : null}
      </div>
      {looking && <Skeleton className="h-[86px] rounded-xl" />}
      {device && !looking && <DeviceSummary device={device} />}
    </div>
  )
}

export function DeviceSummary({ device, compact = false }: { device: PairingLookup; compact?: boolean }) {
  const screens = device.screens ?? []
  return (
    <div className={cn('flex items-start gap-3.5 rounded-xl border bg-muted/30', compact ? 'p-3' : 'p-4', 'animate-in fade-in-0 slide-in-from-bottom-1 duration-200')}>
      <PlatformIcon platform={device.platform} size={compact ? 'default' : 'lg'} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <p className="truncate font-semibold">{device.model ?? platformLabel(device.platform)}</p>
          <span className="inline-flex items-center gap-1 text-xs font-medium text-success">
            <CheckCircle2 className="size-3.5" /> Device found
          </span>
        </div>
        <p className="mt-0.5 text-[13px] text-muted-foreground">
          {platformLabel(device.platform)} · {device.arch}
          {device.player_version && <> · Player v{device.player_version}</>}
        </p>
        {!compact && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
            {screens.length === 0 ? (
              <span className="text-muted-foreground">No screen info reported</span>
            ) : (
              screens.map((s, i) => (
                <span key={String(s.id ?? i)} className="inline-flex items-center gap-1 rounded-md border bg-card px-1.5 py-0.5">
                  <Monitor className="size-3 text-muted-foreground" />
                  {s.width && s.height ? `${s.width}×${s.height}` : 'Screen'}
                  {s.refresh_hz ? ` @${Math.round(s.refresh_hz)}Hz` : ''}
                  {s.connected === false && <span className="text-muted-foreground">(disconnected)</span>}
                </span>
              ))
            )}
            <span className="ml-auto inline-flex items-center gap-1 text-muted-foreground">
              <Clock className="size-3" /> Code expires {formatRelative(device.expires_at)}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------- mode step */

function ModeStep({ mode, onMode, canCreate }: { mode: ClaimMode | null; onMode: (m: ClaimMode) => void; canCreate: boolean }) {
  const options: { mode: ClaimMode; icon: typeof Monitor; title: string; text: string; disabled?: string }[] = [
    { mode: 'new', icon: MonitorCheck, title: 'Create new display', text: 'Set up a brand-new screen with its own name, location and settings.', disabled: canCreate ? undefined : 'Requires permission to manage displays' },
    { mode: 'attach', icon: Link2, title: 'Attach to existing display', text: 'Give a pending or orphaned display its player. It keeps its settings and content.' },
    { mode: 'replace', icon: Replace, title: 'Replace player of a display', text: 'Swap out failed hardware. The old player is retired and its credentials revoked.' },
  ]
  return (
    <div role="radiogroup" aria-label="Pairing mode" className="grid gap-2.5">
      {options.map((o) => {
        const selected = mode === o.mode
        return (
          <button
            key={o.mode}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={!!o.disabled}
            onClick={() => onMode(o.mode)}
            className={cn(
              'flex items-start gap-3.5 rounded-xl border bg-card p-4 text-left transition outline-none hover:border-primary/40 focus-visible:ring-[3px] focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
              selected && 'border-primary bg-primary/[0.04] ring-1 ring-primary',
            )}
          >
            <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}>
              <o.icon className="size-[18px]" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold">{o.title}</span>
              <span className="mt-0.5 block text-[13px] text-muted-foreground">{o.disabled ?? o.text}</span>
            </span>
            <span className={cn('mt-1 flex size-4 shrink-0 items-center justify-center rounded-full border', selected && 'border-primary')}>
              {selected && <span className="size-2 rounded-full bg-primary" />}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/* ----------------------------------------------------------- details step */

function DetailsStep({ state, onChange }: { state: PairingState; onChange: (d: PairingState['display']) => void }) {
  const d = state.display
  const locationOptions = useLocationComboOptions()
  const groupOptions = useGroupOptions()
  const tagOptions = useTagOptions()
  const location = locationOptions.find((o) => o.value === d.location_id)
  const set = (p: Partial<typeof d>) => onChange({ ...d, ...p })
  return (
    <div className="grid gap-4">
      <Field label="Display name" required>
        <Input value={d.name} onChange={(e) => set({ name: e.target.value })} placeholder="Reception display" autoFocus maxLength={120} />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Location" optional>
          <Combobox value={d.location_id} onChange={(v) => set({ location_id: v })} options={locationOptions} placeholder="No location" searchPlaceholder="Search locations…" allowClear />
        </Field>
        <Field label="Orientation">
          <OrientationSelect value={d.orientation} onChange={(orientation) => set({ orientation })} />
        </Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Groups" optional>
          <MultiCombobox value={d.group_ids} onChange={(group_ids) => set({ group_ids })} options={groupOptions} placeholder="No groups" emptyText="No groups yet" />
        </Field>
        <Field label="Tags" optional>
          <MultiCombobox value={d.tag_ids} onChange={(tag_ids) => set({ tag_ids })} options={tagOptions} placeholder="No tags" emptyText="No tags yet" />
        </Field>
      </div>
      <Field label="Timezone">
        <div>
          <InheritableTimezone
            inherit={d.timezone === null}
            onInheritChange={(inherit) => set({ timezone: inherit ? null : browserTimeZone() })}
            value={d.timezone}
            onChange={(timezone) => set({ timezone })}
            inheritedLabel={location ? `From ${location.label} (${location.hint})` : 'From the location, or your organization'}
          />
        </div>
      </Field>
    </div>
  )
}

/* ------------------------------------------------------------ target step */

function displayOption(d: Display): ComboboxOption {
  return { value: d.id, label: d.name, hint: d.location?.name ?? (d.status === 'pending' ? 'Pending' : d.status === 'orphaned' ? 'Orphaned' : undefined), keywords: [d.location?.name ?? ''] }
}

function TargetStep({ state, onDisplay, onReason }: { state: PairingState; onDisplay: (id: string | null) => void; onReason: (r: string) => void }) {
  const replace = state.mode === 'replace'
  const pending = useDisplayPage({ status: 'pending', per_page: 100, sort: 'name' }, !state.locked && !replace)
  const orphaned = useDisplayPage({ status: 'orphaned', per_page: 100, sort: 'name' }, !state.locked && !replace)
  const active = useDisplayPage({ status: 'active', per_page: 100, sort: 'name' }, !state.locked && replace)
  const locked = useDisplay(state.displayId ?? '', state.locked)
  const options = useMemo(() => {
    const list = replace ? (active.data ?? []) : [...(orphaned.data ?? []), ...(pending.data ?? [])]
    return list.filter((d) => (replace ? !!d.player : !d.player)).map(displayOption)
  }, [replace, active.data, orphaned.data, pending.data])
  const loading = replace ? active.isPending : pending.isPending || orphaned.isPending

  return (
    <div className="grid gap-4">
      {state.locked ? (
        locked.data ? (
          <LockedDisplay display={locked.data} />
        ) : (
          <Skeleton className="h-16 rounded-xl" />
        )
      ) : (
        <Field
          label={replace ? 'Display whose player you’re replacing' : 'Display to attach to'}
          hint={replace ? 'Only displays that currently have a player are listed.' : 'Only displays without an active player (pending or orphaned) are listed.'}
        >
          <Combobox
            value={state.displayId}
            onChange={onDisplay}
            options={options}
            placeholder={loading ? 'Loading displays…' : options.length === 0 ? 'No eligible displays' : 'Select a display'}
            searchPlaceholder="Search displays…"
            emptyText="No eligible displays"
            disabled={loading || options.length === 0}
          />
        </Field>
      )}
      {replace && (
        <>
          <Field label="Reason" optional hint="Saved in the player history and audit log.">
            <Textarea rows={2} value={state.reason} maxLength={255} onChange={(e) => onReason(e.target.value)} placeholder="Hardware failure, upgrade…" />
          </Field>
          <Callout>
            The current player will be <span className="font-medium text-foreground">retired</span> and its credentials revoked. The new player receives the exact same configuration and content.
          </Callout>
        </>
      )}
    </div>
  )
}

function LockedDisplay({ display }: { display: Display }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border bg-muted/30 p-3.5">
      <span className="flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Monitor className="size-[18px]" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{display.name}</p>
        <p className="truncate text-xs text-muted-foreground">{display.location?.name ?? 'No location'}</p>
      </div>
      <ConnectivityBadge display={display} />
    </div>
  )
}

function Callout({ children }: { children: ReactNode }) {
  return <div className="rounded-xl border border-info/30 bg-info/8 p-3.5 text-[13px] text-muted-foreground">{children}</div>
}

/* ----------------------------------------------------------- success step */

function SuccessStep({ result }: { result: PairingClaimResult }) {
  const verb = result.mode === 'new' ? 'created and paired' : result.mode === 'replace' ? 'now has a new player' : 'is now paired'
  return (
    <div className="flex flex-col items-center gap-3 py-4 text-center animate-in fade-in-0 zoom-in-[0.98] duration-300">
      <div className="relative">
        <div className="absolute inset-0 rounded-full bg-success/30 blur-xl" aria-hidden />
        <div className="relative flex size-14 items-center justify-center rounded-full bg-success text-white shadow-md">
          <CheckCircle2 className="size-7" />
        </div>
      </div>
      <div>
        <p className="text-lg font-semibold">
          {result.display.name} {verb}
        </p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
          The player picks up its credentials within a few seconds, then downloads its configuration. It will show as online after the first heartbeat.
        </p>
      </div>
      <div className="mt-1 flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-xs">
        <PlatformIcon platform={result.player.platform} size="sm" />
        <span className="font-medium">{result.player.model ?? platformLabel(result.player.platform)}</span>
        {result.player.player_version && <span className="text-muted-foreground">v{result.player.player_version}</span>}
      </div>
    </div>
  )
}
